import React, { useState } from 'react';
import {
  Box, Flex, Heading, Text, Button, Input, Textarea, Select,
  FormControl, FormLabel, FormHelperText, SimpleGrid, Card, CardBody,
  VStack, HStack, useColorModeValue, useToast, Progress, Alert, AlertIcon
} from '@chakra-ui/react';
import { Brain, Upload, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Dashboard/Sidebar';
import SurahAyahSelector from '../components/SurahAyahSelector';
import { createResource } from '../api/resourceService';
import useResourceOptions from '../hooks/useResourceOptions';
import { typeStyle } from '../components/Resources/ResourceBadges';

const EMPTY_FORM = {
  knowledgeType: '', title: '', description: '', usageGuide: '', url: '', tags: '', license: '',
  reciterName: '', file: null,
  reference: { surahId: '', ayahStart: '', ayahEnd: '' },
};

// FastAPI returns a string for most errors and a list for validation errors
const errorMessage = (err) => {
  const detail = err.response?.data?.detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg).join('; ');
  return detail || err.message;
};

const ContributePage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const options = useResourceOptions();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [resourceType, setResourceType] = useState('dataset');
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [submitted, setSubmitted] = useState(null);
  // Changing the key remounts the file input, clearing the name the browser still shows
  const [fileInputKey, setFileInputKey] = useState(0);

  const cardBg = useColorModeValue('white', 'gray.800');
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtextColor = useColorModeValue('gray.600', 'gray.400');

  const active = options.resourceType(resourceType);
  const isUpload = active?.kind === 'file';
  const maxBytes = options.max_file_size_mb * 1024 * 1024;

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));

  const selectType = (key) => {
    setResourceType(key);
    setSubmitted(null);
    setForm((prev) => ({ ...prev, file: null }));
    setFileInputKey((k) => k + 1);
  };

  const validate = () => {
    if (!form.knowledgeType) return 'Please choose the type of knowledge.';
    if (!form.title.trim() || !form.description.trim()) return 'Please give a title and a description.';
    if (!form.usageGuide.trim()) return 'Please explain how people can use this resource.';
    if (isUpload) {
      if (!form.file) return 'Please choose a file to upload.';
      if (form.file.size > maxBytes) return `The file is larger than ${options.max_file_size_mb} MB.`;
    } else if (!/^https?:\/\/\S+$/i.test(form.url.trim())) {
      return 'Please enter a link that starts with http:// or https://';
    }
    return null;
  };

  const handleSubmit = async () => {
    const problem = validate();
    if (problem) {
      toast({ title: problem, status: 'warning', duration: 4000 });
      return;
    }

    const data = new FormData();
    data.append('resource_type', resourceType);
    data.append('knowledge_type', form.knowledgeType);
    data.append('title', form.title.trim());
    data.append('description', form.description.trim());
    data.append('usage_guide', form.usageGuide.trim());
    if (form.tags.trim()) data.append('tags', form.tags.trim());
    if (form.license.trim()) data.append('license', form.license.trim());
    const { surahId, ayahStart, ayahEnd } = form.reference;
    if (surahId) data.append('surah_id', surahId);
    if (surahId && ayahStart) data.append('ayah_start', ayahStart);
    if (surahId && ayahStart && ayahEnd) data.append('ayah_end', ayahEnd);
    if (resourceType === 'audio' && form.reciterName.trim()) data.append('reciter_name', form.reciterName.trim());
    if (isUpload) data.append('file', form.file);
    else data.append('url', form.url.trim());

    setSubmitting(true);
    setProgress(0);
    try {
      const result = await createResource(data, isUpload ? setProgress : undefined);
      setSubmitted(result.resource);
      setForm(EMPTY_FORM);
      setFileInputKey((k) => k + 1);
    } catch (err) {
      toast({ title: 'Submission failed', description: errorMessage(err), status: 'error', duration: 6000 });
    }
    setSubmitting(false);
  };

  return (
    <Flex direction="row" minH="100vh" bg={pageBg}>
      <Sidebar isCollapsed={sidebarCollapsed} toggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)} />

      <Box
        pl={sidebarCollapsed ? '60px' : '250px'}
        w="full" overflowY="auto" pb={8}
        transition="padding-left 0.2s ease"
      >
        {/* Header */}
        <Box px={8} py={6} borderBottom="1px" borderColor={borderColor} bg={cardBg}>
          <HStack>
            <Upload size={28} color="var(--chakra-colors-teal-500)" />
            <Heading size="lg">Share a Resource</Heading>
          </HStack>
          <Text color={subtextColor} mt={1}>
            Share datasets, documents, recitations or links. Experts vote on every resource, and it is
            published once {options.endorsementRule}.
          </Text>
        </Box>

        <Box px={8} py={6} maxW="1000px">
          {submitted && (
            <Alert status="success" borderRadius="lg" mb={6}>
              <AlertIcon />
              <Flex w="full" justify="space-between" align="center" gap={4} wrap="wrap">
                <Box>
                  <Text fontWeight="medium">Thank you, "{submitted.title}" was submitted.</Text>
                  <Text fontSize="sm">It is waiting for expert endorsement.</Text>
                </Box>
                <Button size="sm" leftIcon={<CheckCircle size={14} />} onClick={() => navigate(`/resources/${submitted.id}`)}>
                  View it
                </Button>
              </Flex>
            </Alert>
          )}

          {/* Step 1: type of resource */}
          <Heading size="sm" mb={3}>1. Type of resource</Heading>
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 4 }} spacing={3} mb={3}>
            {options.resource_types.map((t) => {
              const { icon: Icon, color } = typeStyle(t.key);
              const isActive = t.key === resourceType;
              return (
                <Card
                  key={t.key}
                  as="button"
                  type="button"
                  textAlign="left"
                  bg={cardBg}
                  borderRadius="xl"
                  border="2px solid"
                  borderColor={isActive ? `${color}.400` : borderColor}
                  boxShadow={isActive ? 'md' : 'sm'}
                  _hover={{ borderColor: `${color}.300` }}
                  transition="all 0.2s"
                  onClick={() => selectType(t.key)}
                  aria-pressed={isActive}
                >
                  <CardBody p={3}>
                    <HStack>
                      <Icon size={20} color={`var(--chakra-colors-${color}-500)`} />
                      <Text fontWeight="bold" fontSize="sm">{t.label}</Text>
                    </HStack>
                    <Text fontSize="xs" color={subtextColor} mt={1}>{t.description}</Text>
                  </CardBody>
                </Card>
              );
            })}
          </SimpleGrid>
          <Text fontSize="sm" color={subtextColor} mb={6}>
            Sharing a trained AI model?{' '}
            <Button variant="link" size="sm" colorScheme="purple" leftIcon={<Brain size={14} />} onClick={() => navigate('/models/upload')}>
              Use model upload
            </Button>
          </Text>

          {active && (
            <Card bg={cardBg} borderRadius="xl" boxShadow="sm">
              <CardBody>
                <VStack spacing={4} align="stretch">
                  {/* Step 2: type of knowledge */}
                  <Heading size="sm">2. Type of knowledge</Heading>
                  <FormControl isRequired>
                    <FormLabel>What is it about?</FormLabel>
                    <Select
                      placeholder="Choose a knowledge type"
                      value={form.knowledgeType}
                      onChange={(e) => handleChange('knowledgeType', e.target.value)}
                    >
                      {options.knowledge_types.map((k) => <option key={k.key} value={k.key}>{k.label}</option>)}
                    </Select>
                  </FormControl>

                  {/* Step 3: describe it */}
                  <Heading size="sm" pt={2}>3. About this {active.label.toLowerCase()}</Heading>
                  <FormControl isRequired>
                    <FormLabel>Title</FormLabel>
                    <Input
                      value={form.title}
                      maxLength={200}
                      placeholder="e.g. Tafsir of Surah Yusuf (Malay)"
                      onChange={(e) => handleChange('title', e.target.value)}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>Description</FormLabel>
                    <Textarea
                      rows={3}
                      value={form.description}
                      placeholder="What is in it and where it comes from"
                      onChange={(e) => handleChange('description', e.target.value)}
                    />
                  </FormControl>

                  <FormControl isRequired>
                    <FormLabel>How people can use this</FormLabel>
                    <Textarea
                      rows={4}
                      value={form.usageGuide}
                      placeholder={
                        'e.g. Load the CSV with pandas; each row is one ayah with its translation. '
                        + 'Good for training translation models or as a reference for students.'
                      }
                      onChange={(e) => handleChange('usageGuide', e.target.value)}
                    />
                    <FormHelperText>Who it is for, what it is good for, and any steps to get started.</FormHelperText>
                  </FormControl>

                  <SurahAyahSelector
                    value={form.reference}
                    onChange={(reference) => handleChange('reference', reference)}
                  />

                  {resourceType === 'audio' && (
                    <FormControl>
                      <FormLabel>Reciter</FormLabel>
                      <Input
                        value={form.reciterName}
                        maxLength={200}
                        placeholder="Name of the reciter, if known"
                        onChange={(e) => handleChange('reciterName', e.target.value)}
                      />
                    </FormControl>
                  )}

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <FormControl>
                      <FormLabel>Tags</FormLabel>
                      <Input
                        value={form.tags}
                        placeholder="e.g. malay, juz amma, beginner"
                        onChange={(e) => handleChange('tags', e.target.value)}
                      />
                      <FormHelperText>Separate tags with commas</FormHelperText>
                    </FormControl>
                    <FormControl>
                      <FormLabel>Licence or citation</FormLabel>
                      <Input
                        value={form.license}
                        maxLength={200}
                        placeholder="e.g. CC BY 4.0, or how to cite it"
                        onChange={(e) => handleChange('license', e.target.value)}
                      />
                    </FormControl>
                  </SimpleGrid>

                  {/* Step 4: upload or link */}
                  <Heading size="sm" pt={2}>4. {isUpload ? 'Upload the file' : 'Link'}</Heading>
                  {isUpload ? (
                    <FormControl isRequired>
                      <FormLabel>File</FormLabel>
                      <Input
                        key={`${resourceType}-${fileInputKey}`}
                        type="file"
                        accept={active.extensions.join(',')}
                        h="auto"
                        py={3}
                        px={4}
                        border="2px dashed"
                        borderColor={borderColor}
                        borderRadius="lg"
                        onChange={(e) => handleChange('file', e.target.files[0] || null)}
                      />
                      <FormHelperText>
                        {form.file
                          ? `${form.file.name} (${(form.file.size / 1024 / 1024).toFixed(2)} MB)`
                          : `${active.extensions.join(', ')}, up to ${options.max_file_size_mb} MB`}
                      </FormHelperText>
                    </FormControl>
                  ) : (
                    <FormControl isRequired>
                      <FormLabel>Link</FormLabel>
                      <Input
                        type="url"
                        value={form.url}
                        placeholder="https://"
                        onChange={(e) => handleChange('url', e.target.value)}
                      />
                    </FormControl>
                  )}

                  {submitting && isUpload && (
                    <Progress value={progress} size="sm" colorScheme="teal" borderRadius="full" />
                  )}

                  <Flex justify="flex-end">
                    <Button
                      colorScheme="teal"
                      leftIcon={<Upload size={16} />}
                      onClick={handleSubmit}
                      isLoading={submitting}
                      loadingText="Submitting..."
                    >
                      Submit for endorsement
                    </Button>
                  </Flex>
                </VStack>
              </CardBody>
            </Card>
          )}
        </Box>
      </Box>
    </Flex>
  );
};

export default ContributePage;
